/**
 * Location Service
 * 
 * IP detection is handled by our backend (/api/v1/geolocation/detect).
 * No third-party API is called from the frontend.
 * Reverse geocoding still uses Nominatim (only triggered on user gesture).
 */

import { API_BASE_URL, API_VERSION } from '../config/appConfig';

// Default fallback city when backend is unreachable
const DEFAULT_CITY = 'Lucknow';
const DEFAULT_LAT = 26.8467;
const DEFAULT_LNG = 80.9462;

export interface DetectedLocation {
  city: string;
  latitude: number;
  longitude: number;
  source: 'ip' | 'fallback';
}

/**
 * Detect approximate location by calling our backend endpoint.
 * Backend handles: IP extraction → caching → ipapi.co call → fallback.
 * Frontend never touches a third-party geo API directly.
 */
export const detectLocationByIP = async (): Promise<DetectedLocation> => {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);

    const response = await fetch(
      `${API_BASE_URL}/api/${API_VERSION}/geolocation/detect`,
      { signal: controller.signal },
    );
    clearTimeout(timeout);

    if (response.ok) {
      const json = await response.json();
      const d = json.data;
      if (d?.city && d?.latitude && d?.longitude) {
        return {
          city: d.city,
          latitude: d.latitude,
          longitude: d.longitude,
          source: d.source || 'ip',
        };
      }
    }
  } catch {
    // Backend unreachable — fall through to default
  }

  return {
    city: DEFAULT_CITY,
    latitude: DEFAULT_LAT,
    longitude: DEFAULT_LNG,
    source: 'fallback',
  };
};

/**
 * Reverse geocode coordinates to city name.
 * Uses free Nominatim OpenStreetMap API (1 req/sec).
 */
export const reverseGeocode = async (lat: number, lng: number): Promise<string | null> => {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);

    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`,
      {
        headers: { 'Accept-Language': 'en' },
        signal: controller.signal,
      }
    );
    clearTimeout(timeout);

    if (!response.ok) throw new Error('Geocode failed');

    const data = await response.json();
    const address = data.address;

    // Try city → town → village → county → state
    return (
      address?.city ||
      address?.town ||
      address?.village ||
      address?.county ||
      address?.state ||
      null
    );
  } catch {
    return null;
  }
};

/**
 * Popular cities for quick-select in the city selector.
 */
export const POPULAR_CITIES: { name: string; lat: number; lng: number }[] = [
  { name: 'Lucknow', lat: 26.8467, lng: 80.9462 },
  { name: 'Delhi', lat: 28.6139, lng: 77.209 },
  { name: 'Mumbai', lat: 19.076, lng: 72.8777 },
  { name: 'Bangalore', lat: 12.9716, lng: 77.5946 },
  { name: 'Hyderabad', lat: 17.385, lng: 78.4867 },
  { name: 'Chennai', lat: 13.0827, lng: 80.2707 },
  { name: 'Pune', lat: 18.5204, lng: 73.8567 },
  { name: 'Kolkata', lat: 22.5726, lng: 88.3639 },
  { name: 'Ahmedabad', lat: 23.0225, lng: 72.5714 },
];
